import { useMutation } from "@tanstack/react-query";
import { Icon } from "icons";
import { type MouseEvent } from "react";
import { DropdownMenuItem, cn } from "ui";

import { type RowAction } from "src/components/DataTable";
import { DataTableRenderIcon } from "src/components/DataTableRenderIcon";

interface DataTableActionMenuItemProps {
  action: RowAction;
  onDialogOpen: () => void;
  closeDropdown: () => void;
}

export function DataTableActionMenuItem(props: DataTableActionMenuItemProps) {
  const mutation = useMutation({
    mutationFn: props.action.mutationFn ?? (() => Promise.resolve()),
    onSuccess: (data) => {
      props.action.onSuccess?.(data);
      props.closeDropdown();
    },
    onError: (error) => {
      props.action.onError?.(error);
    },
    onSettled: (data, error) => {
      props.action.onSettled?.(data, error);
    },
  });

  function handleClick(e: MouseEvent) {
    e.stopPropagation();
    e.preventDefault();

    if (props.action.dialog) {
      props.onDialogOpen();
      props.closeDropdown();
    } else if (props.action.mutationFn) {
      mutation.mutate();
    } else {
      props.closeDropdown();
      props.action.onSelect?.();
    }
  }

  const isLoading =
    props.action.isLoading ??
    (props.action.mutationFn ? mutation.isPending : false);

  return (
    <DropdownMenuItem
      onClick={handleClick}
      disabled={isLoading}
      className={cn("flex items-center gap-2 whitespace-nowrap", {
        "text-destructive hover:text-destructive!": props.action.destructive,
      })}
    >
      {props.action.content ? (
        props.action.content
      ) : (
        <>
          {isLoading ? (
            <Icon name="tabler-loader-2" className="size-4 animate-spin" />
          ) : (
            <DataTableRenderIcon
              icon={props.action.icon}
              destructive={props.action.destructive}
            />
          )}
          {props.action.label}
        </>
      )}
    </DropdownMenuItem>
  );
}
